import {
  EmptyState,
  ErrorState,
  LoadingState,
  type Locale,
  WithHalfdot,
} from "@elyxeer/ui";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import { VisibilityChip } from "~/components/content/VisibilityChip";
import { EventStateChip } from "~/components/events/EventStateChip";
import { Screen } from "~/components/ui/Screen";
import { useLocale } from "~/i18n";
import { fetchAdminEvents } from "~/lib/api";
import { queryKeys } from "~/lib/query-client";
import type { EventAdminSort } from "~/types/events-admin";
import type { SortOrder } from "~/types/news-admin";

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;
const TABLE_ICON_SIZE = 20;

type VisibilityFilter = "all" | "published" | "hidden";

/** Searchable calendar history and future schedule, including hidden drafts. */
export function EventsRoute() {
  const { t } = useTranslation();
  const { formatDate } = useLocale();
  const navigate = useNavigate();
  const [locale, setLocale] = useState<Locale | undefined>(undefined);
  const [visibility, setVisibility] = useState<VisibilityFilter>("all");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [sortBy, setSortBy] = useState<EventAdminSort>("starts_at");
  const [sortOrder, setSortOrder] = useState<SortOrder>("asc");

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setSearch(searchInput.trim() || undefined);
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [searchInput]);

  const isPublished =
    visibility === "all" ? undefined : visibility === "published";
  const query = useInfiniteQuery({
    queryKey: queryKeys.events({
      locale,
      search,
      isPublished,
      sortBy,
      sortOrder,
    }),
    queryFn: ({ pageParam, signal }) =>
      fetchAdminEvents({
        locale,
        search,
        isPublished,
        sortBy,
        sortOrder,
        limit: PAGE_SIZE,
        offset: pageParam,
        signal,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => {
      const loaded = pages.reduce((sum, page) => sum + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
  });

  const events = query.data?.pages.flatMap((page) => page.items) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;
  const dateOptions = {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  } as const;

  const changeSort = (column: EventAdminSort) => {
    if (sortBy === column) {
      setSortOrder((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(column);
    setSortOrder(column === "title" || column === "starts_at" ? "asc" : "desc");
  };

  return (
    <Screen maxWidth="xl">
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          flexWrap: "wrap",
          marginBottom: 3,
        }}
      >
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="H1" sx={{ color: "text.primary" }}>
            <WithHalfdot size={11}>{t("events.title")}</WithHalfdot>
          </Typography>
          <Typography
            variant="T2"
            sx={{ color: "text.secondary", marginTop: 1 }}
          >
            {t("events.subtitle")}
          </Typography>
        </Box>
        <Button
          component={RouterLink}
          to="/events/new"
          variant="contained"
          startIcon={<AddIcon />}
          data-testid="events.new"
        >
          {t("events.newEvent")}
        </Button>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: "wrap",
          marginBottom: 2,
        }}
      >
        <Tabs
          value={locale ?? "all"}
          onChange={(_event, value: string) =>
            setLocale(value === "all" ? undefined : (value as Locale))
          }
          sx={{ flex: 1, minWidth: 300 }}
        >
          <Tab label={t("events.filter.allLocales")} value="all" />
          <Tab label={t("events.filter.cs")} value="cs" />
          <Tab label={t("events.filter.en")} value="en" />
        </Tabs>

        <TextField
          select
          label={t("events.filter.visibility")}
          size="small"
          value={visibility}
          onChange={(event) =>
            setVisibility(event.target.value as VisibilityFilter)
          }
          slotProps={{
            htmlInput: { "data-testid": "events.filter-visibility" },
          }}
          sx={{ width: { xs: "100%", sm: 190 } }}
        >
          <MenuItem value="all">{t("events.filter.all")}</MenuItem>
          <MenuItem value="published">{t("events.status.published")}</MenuItem>
          <MenuItem value="hidden">{t("events.status.hidden")}</MenuItem>
        </TextField>

        <TextField
          label={t("events.search")}
          type="search"
          size="small"
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: TABLE_ICON_SIZE }} />
                </InputAdornment>
              ),
            },
            htmlInput: { "data-testid": "events.search" },
          }}
          sx={{ width: { xs: "100%", sm: 320 } }}
        />
      </Box>

      {query.isPending ? (
        <LoadingState />
      ) : query.isError ? (
        <ErrorState onRetry={() => void query.refetch()} />
      ) : events.length === 0 ? (
        <EmptyState
          message={t(
            search || locale || visibility !== "all"
              ? "events.emptyFilter"
              : "events.empty",
          )}
        />
      ) : (
        <>
          <Typography
            variant="T3"
            sx={{ color: "text.muted", marginBottom: 1 }}
          >
            {t("events.count", { loaded: events.length, total })}
          </Typography>
          <Box sx={{ overflowX: "auto" }}>
            <Table data-testid="events.table" sx={{ minWidth: 1000 }}>
              <TableHead>
                <TableRow>
                  <TableCell
                    sortDirection={sortBy === "title" ? sortOrder : false}
                    sx={{ width: "34%" }}
                  >
                    <TableSortLabel
                      active={sortBy === "title"}
                      direction={sortBy === "title" ? sortOrder : "asc"}
                      onClick={() => changeSort("title")}
                      data-testid="events.sort-title"
                      sx={{
                        "& .MuiTableSortLabel-icon": {
                          fontSize: TABLE_ICON_SIZE,
                        },
                      }}
                    >
                      {t("events.columns.title")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>{t("events.columns.locale")}</TableCell>
                  <TableCell>{t("events.columns.state")}</TableCell>
                  <TableCell>{t("events.columns.visibility")}</TableCell>
                  <TableCell
                    sortDirection={sortBy === "starts_at" ? sortOrder : false}
                  >
                    <TableSortLabel
                      active={sortBy === "starts_at"}
                      direction={sortBy === "starts_at" ? sortOrder : "asc"}
                      onClick={() => changeSort("starts_at")}
                      data-testid="events.sort-starts-at"
                      sx={{
                        "& .MuiTableSortLabel-icon": {
                          fontSize: TABLE_ICON_SIZE,
                        },
                      }}
                    >
                      {t("events.columns.startsAt")}
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>{t("events.columns.endsAt")}</TableCell>
                  <TableCell align="right">
                    {t("events.columns.registrations")}
                  </TableCell>
                  <TableCell
                    sortDirection={sortBy === "updated_at" ? sortOrder : false}
                  >
                    <TableSortLabel
                      active={sortBy === "updated_at"}
                      direction={sortBy === "updated_at" ? sortOrder : "desc"}
                      onClick={() => changeSort("updated_at")}
                      data-testid="events.sort-updated-at"
                      sx={{
                        "& .MuiTableSortLabel-icon": {
                          fontSize: TABLE_ICON_SIZE,
                        },
                      }}
                    >
                      {t("events.columns.updatedAt")}
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map((event) => (
                  <TableRow
                    key={event.id}
                    hover
                    onClick={() => void navigate(`/events/${event.id}`)}
                    data-testid={`events.row-${event.locale}-${event.slug}`}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell>
                      <Typography variant="L1" sx={{ color: "text.primary" }}>
                        {event.title}
                      </Typography>
                      <Typography
                        variant="T3"
                        component="p"
                        sx={{ color: "text.muted" }}
                      >
                        {event.location || t("events.online")}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={event.locale}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <EventStateChip state={event.state} />
                    </TableCell>
                    <TableCell>
                      <VisibilityChip
                        visible={event.is_published}
                        visibleLabel={t("events.status.published")}
                        hiddenLabel={t("events.status.hidden")}
                      />
                    </TableCell>
                    <TableCell>
                      <time dateTime={event.starts_at}>
                        {formatDate(event.starts_at, dateOptions)}
                      </time>
                    </TableCell>
                    <TableCell>
                      {event.ends_at ? (
                        <time dateTime={event.ends_at}>
                          {formatDate(event.ends_at, dateOptions)}
                        </time>
                      ) : (
                        t("common.notSet")
                      )}
                    </TableCell>
                    <TableCell align="right" data-testid="events.registrations">
                      {event.registration_enabled
                        ? event.registration_count
                        : t("common.notSet")}
                    </TableCell>
                    <TableCell>
                      <time dateTime={event.updated_at}>
                        {formatDate(event.updated_at, dateOptions)}
                      </time>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {query.hasNextPage ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: 3 }}>
              <Button
                variant="outlined"
                onClick={() => void query.fetchNextPage()}
                disabled={query.isFetchingNextPage}
                data-testid="events.load-more"
              >
                {t("events.loadMore")}
              </Button>
            </Box>
          ) : null}
        </>
      )}
    </Screen>
  );
}
